import React, { useState, useEffect } from 'react';
import { Settings, Save, Clock, Link2, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const RoutineSetting = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    class_duration: 60,
    break_duration: 15,
    lunch_break_duration: 60,
    start_time: '09:00',
    end_time: '16:00',
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    periods_per_day: 8
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [centralWorkingDays, setCentralWorkingDays] = useState({ working_days: [], is_configured: false });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
    fetchCentralWorkingDays();
  }, []);

  const fetchCentralWorkingDays = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/working-days/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCentralWorkingDays(response.data);
      // Update settings with central working days
      if (response.data.is_configured) {
        setSettings(prev => ({ ...prev, working_days: response.data.working_days }));
      }
    } catch (error) {
      console.error('Error fetching central working days:', error);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/routine/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching routine settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/routine/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(response.data.message || 'Settings saved successfully!');
    } catch (error) {
      console.error('Error saving routine settings:', error);
      alert(error.response?.data?.detail || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Routine Setting</h1>
          <p className="text-sm text-gray-500 mt-1">Configure routine and schedule settings</p>
        </div>
        <button 
          onClick={handleSave} 
          disabled={saving}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start">
          <Settings className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              These settings will be applied globally to all routines and class schedules. Changes will affect future schedule generation.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading settings...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Time Settings
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input type="time" className="w-full px-3 py-2 border rounded-lg" value={settings.start_time} onChange={(e) => setSettings({ ...settings, start_time: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input type="time" className="w-full px-3 py-2 border rounded-lg" value={settings.end_time} onChange={(e) => setSettings({ ...settings, end_time: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class Duration (minutes)</label>
            <input type="number" className="w-full px-3 py-2 border rounded-lg" value={settings.class_duration} onChange={(e) => setSettings({ ...settings, class_duration: parseInt(e.target.value) })} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Break Duration (minutes)</label>
            <input type="number" className="w-full px-3 py-2 border rounded-lg" value={settings.break_duration} onChange={(e) => setSettings({ ...settings, break_duration: parseInt(e.target.value) })} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lunch Break Duration (minutes)</label>
            <input type="number" className="w-full px-3 py-2 border rounded-lg" value={settings.lunch_break_duration} onChange={(e) => setSettings({ ...settings, lunch_break_duration: parseInt(e.target.value) })} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Periods Per Day</label>
            <input type="number" className="w-full px-3 py-2 border rounded-lg" value={settings.periods_per_day} onChange={(e) => setSettings({ ...settings, periods_per_day: parseInt(e.target.value) })} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-2 border-blue-200">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-semibold">Working Days</h3>
            <span className="text-xs font-normal bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              Central Settings
            </span>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Link2 className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">
                  Working Days is managed centrally
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  All modules use the same settings from Master Settings → Working Days
                </p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Current Working Days:</p>
            <div className="flex flex-wrap gap-2">
              {centralWorkingDays.working_days.map(day => (
                <span key={day} className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                  ✓ {day}
                </span>
              ))}
            </div>
            {!centralWorkingDays.is_configured && (
              <p className="text-xs text-amber-600 mt-3">
                ⚠️ Using default settings. Configure in Master Settings for custom schedule.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => navigate('/master-settings/working-days')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Go to Working Days Settings
          </button>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 font-medium mb-2">Summary</p>
            <div className="space-y-1 text-sm text-gray-600">
              <p>Working Days: {centralWorkingDays.working_days.length} days/week</p>
              <p>Daily Classes: {settings.periods_per_day} periods</p>
              <p>Class Duration: {settings.class_duration} minutes</p>
              <p>Total Hours: {Math.round((settings.periods_per_day * settings.class_duration) / 60)} hours/day</p>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default RoutineSetting;